import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import {
	AnimationContainer,
	AnimationImageAsset,
	AnimationImageElement,
	AnimationPositionElement,
	useAnimationContainerTimeline,
} from '../AnimationCommon';
import { presetColors } from '@client/style/Variables';
import { AnimationElementTreetop } from '../AnimationElementTreetop';
import { AnimationElementLamps } from '../AnimationElementLamps';
import { gsap } from 'gsap';

import Functions from '@client/style/Functions';
import { FlyingCanMessage } from './FlyingCanMessage';
import { SquareButton } from '@client/modules/shared/SquareButton';
import BackPortalSvg from '@client/assets/svgs/animations/back-portal.svg';
import FrontPortalSvg from '@client/assets/svgs/animations/front-portal.svg';
import StarsSvg from '@client/assets/svgs/animations/stars.svg';
import StarsDesktopSvg from '@client/assets/svgs/animations/stars-desktop.svg';
import RainbowSvg from '@client/assets/svgs/animations/rainbow.svg';
import RainbowDesktopSvg from '@client/assets/svgs/animations/rainbow-desktop.svg';

const StyledAnimationContainer = styled(AnimationContainer)`
	background-color: ${presetColors.dust};
	img {
		max-width: 100%;
		height: auto;
	}
	margin-bottom: -15%;

	${Functions.breakpoint('mobile')} {
		margin-bottom: -10%;
	}

	&:has(+ .BreakerModule) {
		padding-bottom: calc(var(--breaker-total) + 15vw);

		${Functions.breakpoint('mobile')} {
			padding-bottom: calc(var(--breaker-total) + 10vw);
		}
	}
`;

const BackPortalContainer = styled.div`
	width: 90vw;
	svg {
		width: 100%;
		height: auto;
	}
	${Functions.breakpoint('mobile')} {
		width: 50vw;
	}
`;

const FrontPortalContainer = styled.div`
	width: 75vw;
	svg {
		width: 100%;
		height: auto;
	}

	${Functions.breakpoint('mobile')} {
		width: 42vw;
	}
`;
const StarsContainer = styled.div`
	width: 55vw;
	svg {
		width: 100%;
		height: auto;
	}
	${Functions.breakpoint('mobile')} {
		width: 34vw;
	}
`;

const RainbowContainer = styled.div`
	width: 60vw;
	svg {
		width: 100%;
		height: auto;
	}
`;

const ButtonPlacer = styled.div`
	height: 100%;
	width: 100%;
	display: flex;
	justify-content: center;
	padding-top: 114%;
	${Functions.breakpoint('mobile')} {
		padding-top: 33%;
	}
`;
const MobileSpan = styled.span`
	display: block;
	${Functions.breakpoint('mobile')} {
		display: none;
	}
`;
const DesktopSpan = styled.span`
	display: none;
	${Functions.breakpoint('mobile')} {
		display: block;
	}
`;

const BushyTop = () => {
	return (
		<>
			<AnimationElementLamps
				variant={4}
				position={{ y: '20%', x: '-43%', breakpoints: { mobile: { scale: 0.5, y: '5%', x: '-40%' } } }}
			/>
			<DesktopSpan>
				<AnimationElementLamps
					variant={2}
					position={{
						breakpoints: {
							mobile: { scale: 0.85, y: '72%', x: '40%', angle: -12 },
							ultra: { scale: 0.85, y: '80%', x: '40%', angle: -12 },
						},
					}}
				/>
				<AnimationElementLamps
					variant={1}
					position={{
						breakpoints: {
							mobile: { scale: 0.85, y: '12%', x: '270%' },
							ultra: { scale: 0.85, y: '12%', x: '380%' },
						},
					}}
				/>
			</DesktopSpan>

			<AnimationElementTreetop
				variant={1}
				position={{
					flip: false,
					angle: 12,
					anchor: 'center',
					x: '-35%',
					scale: 1.15,
					y: '-70%',
					breakpoints: { mobile: { scale: 0.8, y: '-75%' } },
				}}
			></AnimationElementTreetop>
			<AnimationElementTreetop
				variant={5}
				position={{ scale: 1.15, x: '-70%', breakpoints: { mobile: { scale: 0.7, x: '-45%', y: '-20%' } } }}
			></AnimationElementTreetop>
			<AnimationElementTreetop
				variant={5}
				position={{
					flip: true,
					scale: 1.15,
					anchor: 'right',
					y: '15%',
					x: '70%',
					breakpoints: { mobile: { scale: 0.7, x: '62%', y: '-15%' } },
				}}
			></AnimationElementTreetop>
			<DesktopSpan>
				<AnimationElementTreetop
					variant={2}
					position={{
						flip: false,
						angle: -20,
						x: '-35%',
						scale: 1.15,
						y: '-70%',
						breakpoints: {
							mobile: { scale: 0.8, y: '-70%', x: '0%' },
							ultra: { scale: 0.8, y: '-70%', x: '20%' },
						},
					}}
				></AnimationElementTreetop>
			</DesktopSpan>
			<DesktopSpan>
				<AnimationElementTreetop
					variant={2}
					position={{
						flip: false,
						angle: 20,
						x: '-35%',
						scale: 1.15,
						y: '-70%',
						breakpoints: {
							mobile: { scale: 0.8, y: '-70%', x: '100%' },
							ultra: { scale: 0.8, y: '-70%', x: '150%' },
						},
					}}
				></AnimationElementTreetop>
			</DesktopSpan>
			<AnimationElementTreetop
				variant={1}
				position={{
					flip: true,
					angle: -12,
					anchor: 'center',
					x: '-54%',
					scale: 1.15,
					y: '-60%',
					breakpoints: { mobile: { scale: 0.8, y: '-70%' } },
				}}
			></AnimationElementTreetop>
			<AnimationElementTreetop
				variant={3}
				position={{
					flip: false,
					scale: 1.15,
					y: '-60%',
					x: '-42%',
					angle: -5,
					breakpoints: { mobile: { scale: 0.8 } },
				}}
			></AnimationElementTreetop>
			<AnimationElementTreetop
				variant={2}
				position={{ flip: false, scale: 1, y: '-50%', x: '-55%', angle: -5, breakpoints: { mobile: { scale: 0.785 } } }}
			></AnimationElementTreetop>

			<AnimationElementLamps
				variant={5}
				position={{ y: '20%', x: '32.5%', anchor: 'right', breakpoints: { mobile: { scale: 0.5, y: '0%' } } }}
			/>

			<AnimationElementTreetop
				variant={1}
				position={{
					scale: 1.2,
					anchor: 'right',
					flip: false,
					x: '65%',
					y: '-30%',
					angle: 10,
					breakpoints: {
						mobile: { scale: 0.8, x: '45%' },
					},
				}}
			></AnimationElementTreetop>
		</>
	);
};

const Portal = () => {
	const starRef = React.useRef<HTMLDivElement>(null);

	// useEffect(() => {
	// 	const query = gsap.utils.toArray<SVGElement>('.star');

	// 	const animationList: any[] = [];

	// 	query.forEach(el => {
	// 		const delay = Math.random() * 10 + 6;
	// 		const tl = gsap.timeline({ repeat: -1, delay: delay - 5, repeatDelay: delay });
	// 		tl.to(el, {
	// 			transformOrigin: 'center center',
	// 			scale: 0,
	// 			duration: 1,
	// 			ease: 'power1.inOut',
	// 		});
	// 		tl.to(el, {
	// 			transformOrigin: 'center center',
	// 			scale: 1,
	// 			duration: 1,
	// 			ease: 'power1.inOut',
	// 		});
	// 		animationList.push(tl);
	// 	});
	// 	const tween = gsap.to(starRef.current, {
	// 		transformOrigin: 'center center',
	// 		keyframes: {
	// 			scale: [1, 1.01, 1, 0.99, 1],
	// 			easeEach: 'sine.inOut',
	// 		},
	// 		duration: 10,
	// 	});

	// 	return () => {
	// 		animationList.forEach(tl => {
	// 			tl.kill();
	// 		});
	// 		tween.kill();
	// 	};
	// }, []);

	const yVal = 25;
	const yValBig = -12;

	return (
		<>
			<AnimationPositionElement
				position={{
					anchor: 'center',
					x: '-50%',
					y: `${yVal}%`,
					breakpoints: { mobile: { angle: 90, y: `${yValBig}%` } },
				}}
			>
				<BackPortalContainer>
					<BackPortalSvg />
				</BackPortalContainer>
			</AnimationPositionElement>
			<AnimationPositionElement
				position={{
					anchor: 'center',
					x: '-50%',
					y: `${yVal + 20}%`,
					breakpoints: { mobile: { angle: 90, y: `${yValBig + 9}%` }, ultra: { angle: 90, y: `${yValBig + 9}%` } },
				}}
			>
				<FrontPortalContainer>
					<FrontPortalSvg />
				</FrontPortalContainer>
			</AnimationPositionElement>
			<AnimationPositionElement
				position={{
					anchor: 'center',
					x: '-55%',
					y: `${yVal + 35}%`,
					breakpoints: { mobile: { y: `${yValBig + 60}%`, scale: 1.2 } },
				}}
			>
				<StarsContainer>
					<MobileSpan>
						<StarsSvg />
					</MobileSpan>
					<DesktopSpan ref={starRef}>
						<StarsDesktopSvg />
					</DesktopSpan>
				</StarsContainer>
			</AnimationPositionElement>
		</>
	);
};

const Scene = () => {
	const imageRef = useRef<HTMLImageElement | null>(null);
	const image = new AnimationImageAsset('https://www.datocms-assets.com/80292/1686225173-bogescenen-lille.png', {});
	const containerTimeline = useAnimationContainerTimeline();

	useEffect(() => {
		const tween = gsap.to(imageRef.current, {
			scale: 1.04,
			duration: 0.4,
			yoyo: true,
			repeat: -1,
			ease: 'back.in(1, 0.3)',
		});
		containerTimeline.add(tween, 0);
		return () => {
			tween.kill();
		};
	}, []);
	return <AnimationImageElement image={image} ref={imageRef} />;
};

const Rainbows = () => {
	const ref = React.useRef<HTMLDivElement>(null);
	const parentTimeline = useAnimationContainerTimeline();

	React.useEffect(() => {
		const tl = gsap.timeline({ repeat: -1 });
		tl.set(ref.current, { transformOrigin: 'center left' });
		tl.to(ref.current, {
			skewX: -1.2,

			duration: 0.4,
			yoyo: true,
			repeat: -1,
			ease: 'back.in(1, 0.3)',
		});

		parentTimeline.add(tl, 0);
		return () => {
			tl.kill();
		};
	}, []);
	return (
		<RainbowContainer ref={ref}>
			<MobileSpan>
				<RainbowSvg />
			</MobileSpan>
			<DesktopSpan>
				<RainbowDesktopSvg />
			</DesktopSpan>
		</RainbowContainer>
	);
};

export default function IllustrationRegnbuescene(props: any) {
	return (
		<StyledAnimationContainer
			className='colorWrapperAnimatedModule'
			height={4155}
			width={2500}
			breakpoints={{ mobile: { height: 1500, width: 2600 }, ultra: { width: 4000, height: 2308 } }}
		>
			{props.data.flyingBannerMessage.length > 0 && <FlyingCanMessage link={props.data.flyingBannerMessage[0]} />}
			<Portal></Portal>
			<BushyTop></BushyTop>
			<AnimationPositionElement
				position={{
					scale: 0.86,
					y: '120%',
					breakpoints: { mobile: { scale: 0.75, y: '40%', x: '-50%', anchor: 'center' } },
				}}
			>
				<Scene></Scene>
			</AnimationPositionElement>
			<AnimationPositionElement
				position={{
					scale: 1,
					y: '141.5%',
					anchor: 'center',
					x: '19%',
					breakpoints: { mobile: { y: '77%', scale: 0.9, x: '4%' } },
				}}
			>
				<Rainbows></Rainbows>
			</AnimationPositionElement>
			<AnimationPositionElement
				position={{
					scale: 1,
					y: '142.5%',
					anchor: 'center',
					x: '-114%',
					flip: true,
					breakpoints: { mobile: { y: '78%', scale: 0.9, x: '-102%' } },
				}}
			>
				<Rainbows></Rainbows>
			</AnimationPositionElement>
			{props.data.cta && (
				<ButtonPlacer>
					<SquareButton
						colors={{
							bg: presetColors.white,
							fg: presetColors.blackish,
						}}
						link={props.data.cta[0]}
					>
						{props.data.cta[0]?.title}
					</SquareButton>
				</ButtonPlacer>
			)}
		</StyledAnimationContainer>
	);
}
